import { gql, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import type {
  FeatureFlag as FeatureFlagInterface,
  FeatureFlagOption,
} from '@interfaces/organizationFeatureFlag';
import type { TranslatedString } from '@interfaces/shared';
import { InfoOutlined } from '@mui/icons-material';
import { AccountTypes } from '@onevesthq/ov-enums';
import { SegmentedControl } from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { UserContext } from '@providers/userContextProvider';
import { Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { usePermission } from '../../../hooks/usePermission';

export const FETCH_ORGANIZATION_FEATURE_FLAGS = (organizationId: string) => gql`
  query fetchOrganizationFeatureFlags {
    fetchOrganization(organizationId:"${organizationId}") {
      organization {
        id
        availableFeatureFlags
      }
    }
    fetchOrganizationFeatureFlag(organizationId:"${organizationId}") {
      organizationFeatureFlag {
        id
        featureFlags { type enabled }
      }
    }
    fetchFeatureFlags {
      featureFlags {
        value
        group
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

export const UPDATE_ORGANIZATION_FEATURE_FLAG = gql`
  mutation updateOrganizationFeatureFlag(
    $input: UpdateOrganizationFeatureFlagInput!
  ) {
    updateOrganizationFeatureFlag(input: $input) {
      organizationFeatureFlag {
        id
      }
    }
  }
`;

type FeatureFlagState = 'ON' | 'OFF' | 'UNDEFINED';
export type FeatureFlagRow = {
  key: string;
  title: TranslatedString;
  group: string;
  originalState: FeatureFlagState;
  state: FeatureFlagState;
  isEnabled?: boolean;
};

const findFeatureFlagRowState = (
  orgFeatureFlags: FeatureFlagOption[],
  type = '',
): FeatureFlagState => {
  const found = orgFeatureFlags.find((f) => f.type === type);
  if (!found) return 'UNDEFINED';
  return found.enabled ? 'ON' : 'OFF';
};

const findFeatureFlagRowIsEnabled = (
  availableFeatureFlags: string[],
  type = '',
): boolean => availableFeatureFlags.some((x) => x === type);

export const FeatureFlag = ({
  featureFlag,
  onChange,
  underline = false,
  testId,
}: {
  featureFlag?: FeatureFlagRow;
  onChange: (key: string, newState: FeatureFlagState) => void;
  underline?: boolean;
  testId?: string;
}) => {
  const { translateBackendString } = useLocalization();
  const canReadFeatureFlags = usePermission('read:feature_flags');
  const { t } = useTranslation(['shared', 'components']);
  const theme = useTheme();

  if (
    !featureFlag ||
    !canReadFeatureFlags ||
    // OV-19600 Hiding usa margin accounts for the time being
    [
      AccountTypes.USA_MARGIN,
      AccountTypes.USA_JT_WROS_MARGIN,
      AccountTypes.USA_JT_TIC_MARGIN,
      AccountTypes.USA_JT_JTBE_MARGIN,
      AccountTypes.USA_JT_CP_MARGIN,
    ].includes(featureFlag.key as AccountTypes)
  )
    return <></>;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'top',
        borderBottom: underline
          ? `1px solid ${theme.color.outlineVariant}`
          : 'none',
        pb: 3,
        pt: 1,
        gap: 1,
      }}
    >
      {featureFlag ? (
        <Typography variant='labelLarge'>
          {translateBackendString(featureFlag?.title)}
        </Typography>
      ) : (
        <Skeleton height='20px' width='100px' />
      )}
      <Box
        sx={{
          minWidth: '400px',
        }}
      >
        <SegmentedControl
          testId={testId}
          value={featureFlag?.state}
          onChange={(value) =>
            onChange(featureFlag.key, value as FeatureFlagState)
          }
          segments={[
            { label: t('yes'), value: 'ON' },
            { label: t('no'), value: 'OFF' },
            { label: t('inherited'), value: 'UNDEFINED' },
          ]}
        />
        {featureFlag?.state === 'UNDEFINED' &&
          featureFlag?.state === featureFlag?.originalState && (
            <Typography
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
              variant='bodySmall'
              component='div'
              color='textSecondary'
            >
              <InfoOutlined
                sx={{ fontSize: '12px', verticalAlign: 'bottom' }}
              />
              {t('components:featureFlags.inheritedSetTo')}{' '}
              {t(`components:featureFlags.${featureFlag.isEnabled}`)}
            </Typography>
          )}
      </Box>
    </Box>
  );
};

export const useOrganizationFeatureFlags = (): {
  featureFlags: FeatureFlagRow[];
  isFeatureFlagEnabled: (type: string) => boolean;
  loading: boolean;
  updateFeatureFlag: (key: string, newState: FeatureFlagState) => void;
  saveFeatureFlags: () => Promise<void>;
} => {
  const { activeOrganization } = useContext(UserContext);
  const [organizationFeatureFlagId, setOrganizationFeatureFlagId] =
    useState('');
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagRow[]>([]);

  const { loading, data, refetch } = useQuery(
    FETCH_ORGANIZATION_FEATURE_FLAGS(activeOrganization.id ?? ''),
  );

  const [updateOrganizationFeatureFlag, { loading: updateLoading }] =
    useMutation(UPDATE_ORGANIZATION_FEATURE_FLAG, {
      variables: {
        input: {
          organizationFeatureFlagId,
          featureFlags: [] as { type: string; enabled: boolean }[],
        },
      },
    });

  const updateFeatureFlag = (key: string, newState: FeatureFlagState) => {
    const newFeatureFlags = featureFlags.map((ff) => {
      if (ff.key === key) {
        return {
          ...ff,
          state: newState,
        };
      }
      return ff;
    });
    setFeatureFlags(newFeatureFlags);
  };

  const isFeatureFlagEnabled = (type: string) => {
    const found = featureFlags.find((ff) => ff.key === type);
    if (found?.state === 'OFF') return false;
    return found?.isEnabled ?? false;
  };

  const saveFeatureFlags = async () => {
    await updateOrganizationFeatureFlag({
      variables: {
        input: {
          organizationFeatureFlagId,
          featureFlags: featureFlags
            .filter((f) => f.state !== 'UNDEFINED')
            .map((f) => ({ type: f.key, enabled: f.state === 'ON' })),
        },
      },
      onCompleted: () => {
        refetch();
      },
    });
  };

  useEffect(() => {
    if (data) {
      const orgFeatureFlags =
        data.fetchOrganizationFeatureFlag.organizationFeatureFlag.featureFlags;
      const flags = data.fetchFeatureFlags.featureFlags;
      setFeatureFlags((prev) =>
        flags.map((f: FeatureFlagInterface) => {
          const state = findFeatureFlagRowState(orgFeatureFlags, f.value);
          const editedState = prev.find((ff) => ff.key === f.value)?.state;
          return {
            key: f.value,
            title: f.translatedDescription,
            group: f.group,
            originalState: state,
            state: editedState ?? state,
            isEnabled:
              state === 'ON'
                ? true
                : state === 'OFF'
                  ? false
                  : findFeatureFlagRowIsEnabled(
                      data.fetchOrganization.organization.availableFeatureFlags,
                      f.value,
                    ),
          };
        }),
      );
      setOrganizationFeatureFlagId(
        data.fetchOrganizationFeatureFlag.organizationFeatureFlag.id,
      );
    }
  }, [data]);

  return {
    featureFlags,
    loading: updateLoading || loading,
    updateFeatureFlag,
    isFeatureFlagEnabled,
    saveFeatureFlags,
  };
};
